/**
 * The server's side of saving an MCE upload: a build that stores nothing, so a
 * bad upload is refused before a new adapter exists, then `PUT /adapters/:id/cdo`
 * with the precondition. The server rebuilds and stores its own build.
 */
import * as flatbuffers from "flatbuffers";
import { unwrapEnvelope } from "../../flatbuffers/envelope";
import { MceIdlBuildRequestT } from "../../flatbuffers/generated/api/mce-idl-build-request";
import { MceIdlBuildResponse } from "../../flatbuffers/generated/api/mce-idl-build-response";
import { Severity } from "../../flatbuffers/generated/mce/severity";
import { sha256Hex } from "../../flatbuffers/sha256";
import { refusedServersIn, serversMessage } from "../serverScopeRefusal";
import { base64ToBytes, bytesToBase64 } from "../base64";
import { fetchFlatBufferBinary, sendFlatBufferBinary } from "../flatbufferApi";
import { asString, packBuildRequestFromMapping, unpackMapping, unpackResult } from "./messages";

const MAX_LOGGED_TEXT = 2000;
const COULD_NOT_SAVE =
  "The mapping could not be saved. Try again. If this continues, click Get log and send the file to technical support.";
const FILES_REFUSED =
  "The server found errors in these files. Click Get log for the details, fix the files and upload them again.";
const STATUS_MESSAGES: Record<number, string> = {
  401: "You do not have permission to save MCE adapters.",
  403: "You do not have permission to save MCE adapters.",
  409: "This mapping cannot replace the adapter's current one. Reload the page and try again. If this continues, click Get log and send the file to technical support.",
  412: "This adapter changed since you opened it. Reload the page and upload the files again.",
  413: "These files are too large to save.",
  422: FILES_REFUSED,
  428: "This adapter changed since you opened it. Reload the page and upload the files again.",
  503: "The server cannot check MCE files right now. Try again in a few minutes.",
};

/** One plain message to show, and what Get log records about it. */
export interface MceSaveFailure {
  message: string;
  detail: string;
  /** The write landed and only what followed it failed. */
  saved?: boolean;
}

/** The precondition names the bytes this page rendered, or none when the row held none. */
export async function mceIfMatch(renderedBase64: string | null): Promise<string> {
  if (!renderedBase64) return '"mce-cdo:none"';
  return `"mce-cdo:${await sha256Hex(base64ToBytes(renderedBase64))}"`;
}

/** The converter's findings in a build response, as text for the log. */
function findingsIn(body: Uint8Array): string[] {
  const buffer = new flatbuffers.ByteBuffer(unwrapEnvelope(body));
  const response = MceIdlBuildResponse.getRootAsMceIdlBuildResponse(buffer).unpack();
  return unpackResult(Uint8Array.from(response.message))
    .diagnostics.filter((finding) => finding.severity === Severity.Error)
    .map((finding) => `${finding.code} line ${finding.line}: ${asString(finding.message)}`);
}

/** The servers a refusal's JSON body names. */
export function refusedServers(text: string): string[] {
  try {
    return refusedServersIn(JSON.parse(text));
  } catch {
    return [];
  }
}

async function failureFrom(step: string, response: Response): Promise<MceSaveFailure> {
  const body = new Uint8Array(await response.arrayBuffer());
  const text = new TextDecoder().decode(body);
  let detail = text.slice(0, MAX_LOGGED_TEXT);
  const servers = response.status === 409 ? refusedServers(text) : [];
  if (servers.length > 0) {
    return { message: serversMessage(servers), detail: `${step}: HTTP 409\n${detail}` };
  }
  if (response.status === 422) {
    try {
      detail = findingsIn(body).join("\n");
    } catch {
      // A 422 with no build response keeps its text.
    }
  }
  return {
    message: STATUS_MESSAGES[response.status] ?? COULD_NOT_SAVE,
    detail: `${step}: HTTP ${response.status}\n${detail}`,
  };
}

function networkFailure(step: string, error: unknown): MceSaveFailure {
  return { message: COULD_NOT_SAVE, detail: `${step}: ${String(error)}` };
}

/** Ask the server to build the mapping's inputs. Stores nothing. Null when it built. */
export async function checkMappingOnServer(
  apiBase: string,
  token: string,
  binaryBase64: string,
): Promise<MceSaveFailure | null> {
  const mapping = unpackMapping(base64ToBytes(binaryBase64));
  // Overrides travel deflated inside the Mapping; the save's own rebuild checks those.
  if (!mapping.idl || mapping.overrides) return null;
  const builder = new flatbuffers.Builder(1024);
  const request = new MceIdlBuildRequestT(Array.from(packBuildRequestFromMapping(mapping)));
  builder.finish(request.pack(builder));
  try {
    const response = await sendFlatBufferBinary(
      `${apiBase}/mce-idl/build`,
      builder.asUint8Array(),
      token,
      "POST",
    );
    if (!response.ok) return await failureFrom("build", response);
    const findings = findingsIn(new Uint8Array(await response.arrayBuffer()));
    return findings.length > 0
      ? { message: FILES_REFUSED, detail: `build: ${findings.join("\n")}` }
      : null;
  } catch (error) {
    return networkFailure("build", error);
  }
}

/** Store the mapping on an adapter. Null when it was saved. */
export async function saveUploadedMapping(
  url: string,
  token: string,
  binaryBase64: string,
  replacesBase64: string | null,
): Promise<MceSaveFailure | null> {
  try {
    const response = await sendFlatBufferBinary(url, base64ToBytes(binaryBase64), token, "PUT", {
      "If-Match": await mceIfMatch(replacesBase64),
    });
    return response.ok ? null : await failureFrom("save", response);
  } catch (error) {
    return networkFailure("save", error);
  }
}

/**
 * What the server stored: its own rebuild, which the next precondition must name.
 * Null when it cannot be read; the save itself has already succeeded by then.
 */
export async function readStoredMapping(url: string, token: string): Promise<string | null> {
  try {
    const bytes = await fetchFlatBufferBinary(url, token);
    return bytes && bytes.length > 0 ? bytesToBase64(unwrapEnvelope(bytes)) : null;
  } catch {
    return null;
  }
}

/** The saved mapping could not be read back, so the page's copy is not the server's. */
export const READ_BACK_FAILED =
  "The mapping was saved, but this page could not read it back. Reload the page before changing it again.";
